import { INVITATION_ID, INVITATION_LINK_ID } from '../../components/org/constants/invitation'
import { LOGGED_OUT_ONLY_PATHS } from '@dtx-company/true-common/src/constants/layout'
import { ProUpgradeSourceIds } from '@dtx-company/app/shared/code/src/components/ProUpradeModal/ProUpgradeModal.types'
import { RootPageState } from '../../redux/types'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { SLUG_RESERVED } from '@dtx-company/flow-codegen/src/page/queries'
import { SlugReservedQuery } from '@dtx-company/flow-codegen/src/page/generated.types'
import { UNIFIED_ASSET_MANAGEMENT } from '@dtx-company/true-common/src/constants/featureFlags'
import { clearPlaceholders } from '../../redux/slices/flowpageFunnelSlice'
import { pageGqlFetcher } from '../../services/gqlFetcher'
import { sendErrorNotification } from '../../utils/notifications'
import { useAuthState } from '../../hooks/useAuthState'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCurrentPage } from '../../redux/slices/utils/currentPage'
import { useDispatch } from 'react-redux'
import { useFlowFeature } from '../useFlowFeature/useFlowFeature'
import { useHandleFlowcodeUserRedirect } from '../useHandleFlowcodeUserRedirect'
import { useIsAccountPaused } from '../useIsAccountPaused'
import { useOpenProUpgradeModal } from '@dtx-company/app/shared/code/src/components/ProUpradeModal/ProUpgradeModal.hooks'
import { usePageSelector } from '../usePageSelector'
import { useRedirectToPausedAccountPage } from '../useRedirectToPausedAccountPage'
import { useRouter } from 'next/router'
import { useSavePage } from '../../redux/slices/utils'

const ssoRoutes = [Routes.SIGN_IN, Routes.SIGN_UP, Routes.ACCEPT_INVITE]

export function useRedirectOnAuthChanges(
  authRequired = false,
  doNotRedirectOnAuthChange = false,
  shouldRedirectToSignUp = false,
  unAuthenticatedRedirectTo = ''
): void {
  const router = useRouter()
  const currentPageIsSSORoute =
    router.pathname === Routes.ACCEPT_INVITE ? false : ssoRoutes.includes(router.pathname as Routes)
  const currentPageIsSSOLogin = router.pathname === Routes.SSO_LOGIN
  const redirectFromSSORoute = useRedirectFromSSORoute()
  const redirectToSignIn = useRedirectToSignIn()
  const redirectToSignUp = useRedirectToSignUp()
  const isAccountPaused = useIsAccountPaused()
  const redirectToPausedAccountPage = useRedirectToPausedAccountPage()
  const [redirectCalled, setRedirectCalled] = useState(false)
  const { isAuthenticated, jwt, isAuthChecked } = useAuthState()

  const orgStatus = jwt?.org?.status
  const comingFromFlowpage = router.query.flowpage === 'true'
  const isFlowpageSignin = router.asPath.includes(Routes.SIGN_IN) && comingFromFlowpage
  const currentPage = useCurrentPage()
  const currentPageSlugName = currentPage?.slugName ?? ''
  const invitationId = (router.query.invitationId ?? '')?.toString()
  const invitationLinkId = (router.query.inviteId ?? '')?.toString()

  const currentPageExists = Boolean(currentPageSlugName) || Boolean(currentPage)
  const { userHasReachedMaxPages } = usePageSelector(
    (state: RootPageState) => state.fpFunnelReducer
  )
  const hasCheckedMaxPagesCompleted =
    comingFromFlowpage && currentPageExists && isFlowpageSignin
      ? userHasReachedMaxPages !== undefined
      : true
  const [isUnifiedAssetManagementEnabled] = useFlowFeature(UNIFIED_ASSET_MANAGEMENT, {
    autoUpdate: true
  })

  useEffect(() => {
    if (!isAuthChecked) return
    if (redirectCalled) return
    if (doNotRedirectOnAuthChange) return
    if (isAuthenticated && currentPageIsSSORoute && hasCheckedMaxPagesCompleted) {
      redirectFromSSORoute()
      setRedirectCalled(true)
    } else if (!isAuthenticated && isAuthChecked && unAuthenticatedRedirectTo) {
      window.location.href = unAuthenticatedRedirectTo
      setRedirectCalled(true)
    } else if (authRequired && !isAuthenticated && isAuthChecked) {
      if (invitationId && router.pathname === Routes.ACCEPT_INVITE) {
        localStorage.setItem(INVITATION_ID, invitationId?.toString())
      }
      if (invitationLinkId && router.pathname === Routes.ACCEPT_ORG_INVITE) {
        localStorage.setItem(INVITATION_LINK_ID, invitationLinkId?.toString())
      }
      if (shouldRedirectToSignUp) {
        redirectToSignUp()
      } else {
        redirectToSignIn()
      }
      setRedirectCalled(true)
    } else if (isAccountPaused && authRequired) {
      redirectToPausedAccountPage()
      setRedirectCalled(true)
    } else if (isAuthenticated && !comingFromFlowpage) {
      if (LOGGED_OUT_ONLY_PATHS.includes(router.pathname as Routes)) {
        router.push(isUnifiedAssetManagementEnabled ? Routes.ASSETS : Routes.CODES)
        setRedirectCalled(true)
      }
    }
  }, [
    redirectCalled,
    setRedirectCalled,
    currentPageIsSSOLogin,
    isAuthChecked,
    isAuthenticated,
    router,
    currentPageIsSSORoute,
    redirectFromSSORoute,
    authRequired,
    redirectToSignIn,
    hasCheckedMaxPagesCompleted,
    doNotRedirectOnAuthChange,
    comingFromFlowpage,
    orgStatus,
    invitationId,
    invitationLinkId,
    redirectToSignUp,
    shouldRedirectToSignUp,
    isAccountPaused,
    redirectToPausedAccountPage,
    unAuthenticatedRedirectTo,
    isUnifiedAssetManagementEnabled
  ])
}

function useRedirectFromSSORoute(): () => void {
  const router = useRouter()
  const comingFromFlowpage = router.query.flowpage === 'true'
  const currentPageIsSignup = router.asPath.includes(Routes.SIGN_UP)
  const isFlowpageSignin = router.asPath.includes(Routes.SIGN_IN) && comingFromFlowpage
  const currentPage = useCurrentPage()
  const currentPageSlugName = currentPage?.slugName ?? ''
  const { jwt } = useAuthState()
  const dispatch = useDispatch()
  // User has a page in redux (built a page while logged out)
  const shouldRedirectToFlowpageProfile = Boolean(currentPageSlugName) || Boolean(currentPage)

  const profileRedirectQuery = useMemo(
    () => (currentPageIsSignup ? { postPublish: true } : {}),
    [currentPageIsSignup]
  )

  const handleFlowcodeUserRedirect = useHandleFlowcodeUserRedirect()
  const { userHasReachedMaxPages } = usePageSelector(
    (state: RootPageState) => state.fpFunnelReducer
  )
  const shouldCheckMaxPages = isFlowpageSignin && shouldRedirectToFlowpageProfile
  const savePage = useSavePage()
  const { handleProtectedActionClick: handleMaxPagesCheck } = useOpenProUpgradeModal({
    openProModalCondition: Boolean(userHasReachedMaxPages),
    sourceId: ProUpgradeSourceIds.FLOWPAGE_SSO_MAX_PAGES,
    onClose: () => {
      router.push(Routes.PAGES)
    },
    action: async ({ isPaymentSuccessful }: { isPaymentSuccessful: boolean }) => {
      if (isPaymentSuccessful) {
        const savedPage = await savePage()
        router.push({
          pathname: Routes.PAGE_EDITOR,
          query: { ...profileRedirectQuery, id: savedPage?.id }
        })
        return
      } else {
        sendErrorNotification('Error processing payment')
      }
      router.push(Routes.PAGES)
    }
  })

  const redirectFromSSORoute = useCallback(async () => {
    const redirectPath = decodeURIComponent(router?.query?.redirectTo as string)
    if (userHasReachedMaxPages && shouldCheckMaxPages) {
      handleMaxPagesCheck()
      return
    }
    if (shouldRedirectToFlowpageProfile) {
      const { slugReserved } = await pageGqlFetcher<SlugReservedQuery>(
        SLUG_RESERVED,
        { slug: currentPageSlugName },
        jwt?.token
      )

      let pageId = currentPage?.id

      if (!slugReserved) {
        const savedPage = await savePage()
        if (savedPage) {
          pageId = savedPage?.id
        }
        dispatch(clearPlaceholders({}))
      }
      if (redirectPath && redirectPath !== 'undefined') {
        const existingQueryParams = router.query
        delete existingQueryParams['redirectTo']

        let nextParams = {}
        let path = redirectPath

        const splitParams = redirectPath.split('&')
        if (splitParams[0]) {
          const splitPathAndFirstParam = splitParams[0].split('?')
          if (splitPathAndFirstParam[0] && splitPathAndFirstParam[1]) {
            path = splitPathAndFirstParam[0]
            splitParams.unshift(splitPathAndFirstParam[1])
            const parsedParams = splitParams.reduce((acc: Record<string, string>, curr: string) => {
              if (curr) {
                const [key, value] = curr.split('=')
                acc[key] = value
              }
              return acc
            }, {})
            nextParams = Object.assign({}, existingQueryParams, parsedParams)
          }
        }

        router.push({
          pathname: path,
          query: nextParams
        })
      } else {
        router.push({
          pathname: Routes.PAGE_EDITOR,
          query: { ...profileRedirectQuery, id: pageId }
        })
      }
    } else if (comingFromFlowpage) {
      router.push(Routes.PAGES)
    } else {
      handleFlowcodeUserRedirect()
    }
  }, [
    router,
    userHasReachedMaxPages,
    shouldCheckMaxPages,
    shouldRedirectToFlowpageProfile,
    comingFromFlowpage,
    handleMaxPagesCheck,
    currentPageSlugName,
    jwt?.token,
    currentPage?.id,
    savePage,
    dispatch,
    profileRedirectQuery,
    handleFlowcodeUserRedirect
  ])

  return redirectFromSSORoute
}

function useRedirectToSignIn(): () => void {
  const router = useRouter()
  const redirectToSignIn = useCallback(() => {
    router.push({
      pathname: Routes.SIGN_IN,
      query: {
        ...router.query,
        redirectTo: encodeURIComponent(router.asPath)
      }
    })
  }, [router])

  return redirectToSignIn
}

function useRedirectToSignUp(): () => void {
  const router = useRouter()
  const redirectToSignIn = useCallback(() => {
    router.push({
      pathname: Routes.SIGN_UP,
      query: {
        ...router.query,
        redirectTo: encodeURIComponent(router.asPath)
      }
    })
  }, [router])

  return redirectToSignIn
}
